import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';

import { InvoiceService } from 'src/app/data/service/invoice.service';
import { TransactionLedgerRow } from 'src/app/data/model/transaction-ledger';
import { ManualCodingPayload } from 'src/app/shared/components/manual-coding-modal/manual-coding-modal.component';
import { ToasterMessageService } from 'src/app/shared/toaster-message/toaster-message.service';
import { ToasterTypes } from 'src/app/core/enum/toaster-type.enum';
import { rowKey } from './transaction-row.util';

export interface TransactionCodingOutcome {
  requested: number;
  succeeded: number;
  failed: number;
}

/**
 * Codes ledger rows from the coding modal, and updates them from what came back.
 *
 * SD-1227: one place for it, so the Transactions screen and a report's coding view code a
 * transaction the same way and report the result in the same words.
 */
@Injectable({
  providedIn: 'root'
})
export class TransactionCodingService {

  constructor(
    private _invoiceService: InvoiceService,
    private _toasterMessageService: ToasterMessageService
  ) { }

  /**
   * Applies `payload` to its transactions, then patches the matching `rows` in place.
   *
   * The rows are patched rather than refetched. The endpoint reports per-transaction results
   * and a 200 does not mean every row succeeded, so the response is the only thing that knows
   * which ones did -- and a reload straight after it showed the rows exactly as they were,
   * leaving the screen to be refreshed by hand before any of it appeared.
   *
   * Never errors: a failure is toasted here and comes back as nothing succeeded, so the
   * caller only has to clear its "Coding…" state.
   */
  code(organisationId: string, payload: ManualCodingPayload, rows: TransactionLedgerRow[]):
    Observable<TransactionCodingOutcome> {

    const ids = payload?.transactionIds || [];

    return this._invoiceService.bulkCodeTransactions({
      organisationId,
      userId: Number(localStorage.getItem('UserId')),
      transactionIds: ids,
      shared: payload.shared,
    }).pipe(
      take(1),
      map((response: any) => this.applyResponse(response, payload, ids, rows)),
      catchError(error => {
        this.showError('Those transactions could not be coded.', error);
        return of({ requested: ids.length, succeeded: 0, failed: ids.length });
      })
    );
  }

  private applyResponse(response: any, payload: ManualCodingPayload, ids: string[],
                        rows: TransactionLedgerRow[]): TransactionCodingOutcome {
    const results = Array.isArray(response?.results) ? response.results : [];

    results.forEach((result: any) => {
      if (result?.status !== 'success' || !result?.transactionId) { return; }
      this.patchCodedRow(rows, result, payload.shared);
    });

    // Without a results array, a 200 is all the endpoint has told us.
    if (!results.length) {
      ids.forEach(id => this.patchCodedRow(rows, { transactionId: id, invoiceId: null }, payload.shared));
    }

    const requested = response?.requestedCount ?? ids.length;
    const succeeded = results.length
      ? results.filter((r: any) => r?.status === 'success').length
      : (response?.succeededCount ?? ids.length);
    const failed = Math.max(requested - succeeded, 0);

    if (succeeded === 0) {
      this.showError(`None of those ${requested} transactions could be coded.`, null);
      return { requested, succeeded, failed };
    }

    this._toasterMessageService.displayToasterState(
      failed > 0 ? ToasterTypes.Warning : ToasterTypes.Success,
      failed > 0 ? 'Partly coded' : 'Coded',
      failed > 0
        ? `${succeeded} of ${requested} coded · ${failed} skipped.`
        : succeeded === 1 ? 'Transaction coded.' : `${succeeded} transactions coded.`);

    return { requested, succeeded, failed };
  }

  /**
   * Brings one row up to date with the coding just applied to it.
   *
   * Tax is deliberately left alone: the payload carries a tax rate id and the column shows a
   * name, and a wrong name is worse than the one that is already there. The next fetch fills
   * it, along with the account code.
   */
  private patchCodedRow(rows: TransactionLedgerRow[],
                        result: { transactionId: string; invoiceId?: string }, shared: any): void {
    const target = rowKey(result.transactionId);
    const row = rows.find(r => rowKey(r.unreconciledReportId) === target);
    if (!row) { return; }

    if (result.invoiceId) { row.relatedID = result.invoiceId; }
    row.isReconciled = true;
    row.hasSuggestedMatch = false;

    if (shared?.supplierName) { row.relatedName = shared.supplierName; }
    if (shared?.accountName) { row.codedAccountName = shared.accountName; }
    if (shared?.details) { row.codedDetails = shared.details; }
  }

  private showError(message: string, error: any): void {
    this._toasterMessageService.displayToasterState(
      ToasterTypes.Error, 'Error', (error && error.error && error.error.errorMessage) || message);
  }
}
